import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { formatFrequencyText } from 'utils/creditsUtils';
import {
  CreditPill,
  CategoryEarnRatePill,
  CardInfoGrid,
  CardInfoFlex,
  CardInfoBlock,
} from './index';
import { formatRewardsType } from '../data/shared/utils/formatRewardsType';
import { formatSpendCategory } from '../data/shared/utils/formatSpendCategory';
import { formatEarnRate } from '../data/shared/utils/formatEarnRate';
import {
  CardData,
  SignupBonus,
  SignupBonusReward,
  CardRewardsEarnRate,
} from 'data/types/CardData';
import { CreditCardRewardsType } from 'data/shared/__autogenerated__/types/CreditCardRewardsTypes';
import {
  isQuarterlyCategoriesEarnRate,
  isSelectableCategoriesEarnRate,
  isFixedCategoryGroupEarnRate,
  isAdvancedCategory,
} from 'data/types/utils';
import { categoryEmojiMap } from '../data/shared/utils/CategoryEmojiMap.web';
import { SelectableCategoriesEarnRate } from 'data/types';
import { flattenRewardsEarnRates } from 'data/shared/utils';
import {
  getCurrentQuarter,
  getSelectedCategoriesForRate,
  useCategoryEarnRatesAndCreditsV2,
} from 'data/shared/hooks/useBestEarnRatesV2';
import { EditPointValues } from '../components/EditPointValues';
import { EarnRateConditionsMessage } from '../components/EarnRateConditionsMessage';
import { BiltSpendRatioModal } from '../components/BiltSpendRatioModal';
import { PromotionalType } from 'data/shared/__autogenerated__/types/PromotionalType';
import {
  promotionalTypesConfig,
  PREFERRED_REWARDS_TIER_NAMES,
  BILT_SPEND_RATIO_TIER_NAMES,
  getApplicablePromotionalTypes,
  groupPromotionalTypesByFamily,
} from 'data/shared/promotionalTypes';
import { CreditCard } from 'data/shared/CreditCard';
import { CategoryType } from 'data/shared/__autogenerated__/types/CreditCardCategoryTypes';

// Helper function to get category emoji
const getCategoryEmoji = (
  category: string
): React.ReactNode | string | undefined => {
  // Try exact match first
  if (categoryEmojiMap[category]) {
    return categoryEmojiMap[category];
  }

  // Try parent categories by removing segments from right to left
  const segments = category.split('.');
  while (segments.length > 1) {
    segments.pop();
    const parentCategory = segments.join('.');
    if (categoryEmojiMap[parentCategory]) {
      return categoryEmojiMap[parentCategory];
    }
  }

  // Return fallback if no match found
  return '📦';
};

// Helper function to extract end date from earn rate description
const extractEndDate = (description: string | null): string | null => {
  if (!description) {
    return null;
  }

  // Look for various end date patterns in the description
  const endDatePatterns = [
    /valid through (\d{1,2}\/\d{1,2}\/\d{4})/i,
    /through (\d{1,2}\/\d{1,2}\/\d{4})/i,
    /until (\d{1,2}\/\d{1,2}\/\d{4})/i,
    /expires (\d{1,2}\/\d{1,2}\/\d{4})/i,
    /expires (\d{1,2}\/\d{1,2}\/\d{4})/i,
    /continues beyond (\d{1,2}\/\d{1,2}\/\d{4})/i,
    /excluded starting (\d{1,2}\/\d{1,2}\/\d{4})/i,
    /starting (\d{1,2}\/\d{1,2}\/\d{4})/i,
  ];

  for (const pattern of endDatePatterns) {
    const match = description.match(pattern);
    if (match) {
      return match[1];
    }
  }

  return null;
};

// Helper function to format signup bonus by aggregating rewards by type
const formatSignupBonus = (
  signupBonus: SignupBonus[] | null | undefined,
  showReviewIndicators = false
): string | React.ReactNode => {
  if (!signupBonus || signupBonus.length === 0) {
    return 'None';
  }

  // Aggregate rewards by type across all tiers
  const aggregatedRewards: Record<string, number> = {};

  const collectRewards = (
    rewards: SignupBonusReward[] | null | undefined
  ): void => {
    if (!rewards) {
      return;
    }

    rewards.forEach((reward) => {
      if (reward?.amount && reward.value_unit) {
        const unit = reward.value_unit.toLowerCase();
        const key = unit === 'dollars' ? 'cash_back' : unit;
        aggregatedRewards[key] = (aggregatedRewards[key] ?? 0) + reward.amount;
      }
    });
  };

  signupBonus.forEach((bonus) => {
    collectRewards(bonus.rewards);
    bonus.tiers?.forEach((tier) => collectRewards(tier.rewards));
  });

  // Format the aggregated rewards into a readable string
  const formattedRewards: string[] = [];

  Object.entries(aggregatedRewards).forEach(([type, amount]) => {
    if (type === 'cash_back') {
      formattedRewards.push(`$${amount.toLocaleString()} cash back`);
    } else if (type === 'points') {
      formattedRewards.push(`${amount.toLocaleString()} points`);
    } else if (type === 'miles') {
      formattedRewards.push(`${amount.toLocaleString()} miles`);
    } else if (type === 'nights') {
      formattedRewards.push(`${amount.toLocaleString()} free nights`);
    } else {
      // For other reward types, use the original unit
      formattedRewards.push(`${amount.toLocaleString()} ${type}`);
    }
  });

  if (formattedRewards.length === 0) {
    return 'None';
  }

  if (formattedRewards.length === 1) {
    return `Up to ${formattedRewards[0]}`;
  }

  // Join multiple rewards with "and"
  const lastReward = formattedRewards.pop();
  return `Up to ${formattedRewards.join(', ')} and ${lastReward}`;
};

interface CardDetailsPanelProps {
  cardInfo: CardData;
  currentSelectedCategories?: string[][][];
  onSelectCategoriesForCard?: (selectedCategories: string[][][]) => void;
  pointValues?: Record<string, number>;
  setShowEditPointValues?: (show: boolean) => void;
  showEditPointValues?: boolean;
  setPointValues?: (rewardsType: CreditCardRewardsType, value: string) => void;
  resetPointValues?: () => void;
  showReviewIndicators?: boolean;
  promotionalTypes?: PromotionalType[];
  setPromotionalTypes?: (types: PromotionalType[]) => void;
  resetPromotionalTypes?: () => void;
}

export const CardDetailsPanel: React.FC<CardDetailsPanelProps> = ({
  cardInfo,
  currentSelectedCategories,
  onSelectCategoriesForCard,
  pointValues = {},
  setShowEditPointValues,
  showEditPointValues = false,
  setPointValues,
  resetPointValues,
  showReviewIndicators = false,
  promotionalTypes = [],
  setPromotionalTypes,
  resetPromotionalTypes,
}) => {
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>(
    {}
  );
  const [localSelectedCategories, setLocalSelectedCategories] =
    useState<string[][][]>(currentSelectedCategories ?? []);
  const [showBiltSpendRatioModal, setShowBiltSpendRatioModal] = useState(false);

  useEffect(() => {
    if (currentSelectedCategories === undefined) {
      return;
    }
    if (
      currentSelectedCategories.length === 0 &&
      localSelectedCategories.length > 0
    ) {
      return;
    }
    setLocalSelectedCategories(currentSelectedCategories);
  }, [currentSelectedCategories, localSelectedCategories.length]);

  const resolvedSelectedCategories =
    currentSelectedCategories && currentSelectedCategories.length > 0
      ? currentSelectedCategories
      : localSelectedCategories;

  // Get all unique categories from the card's earn rates for calculating rewards summary
  const allCategories = useMemo(() => {
    const categories = new Set<CategoryType>();
    cardInfo.rewards_earn_rates?.forEach((rate) => {
      if (
        rate.category &&
        rate.category !== 'selectable_categories' &&
        rate.category !== 'quarterly_categories' &&
        rate.category !== 'fixed_category_group'
      ) {
        categories.add(rate.category as CategoryType);
      }
    });
    return Array.from(categories);
  }, [cardInfo.rewards_earn_rates]);

  // Get all promotional types that are applicable to this specific card
  const allApplicablePromotionalTypes = useMemo(() => {
    return getApplicablePromotionalTypes([cardInfo.id as CreditCard]);
  }, [cardInfo.id]);

  // Get promotional types that are both applicable AND selected by the user
  const applicablePromotionalTypesForCard = useMemo(() => {
    if (!promotionalTypes || promotionalTypes.length === 0) {
      return [];
    }
    // Filter to only include promotional types that are both:
    // 1. Set by the user (in promotionalTypes)
    // 2. Applicable to this card (in allApplicablePromotionalTypes)
    return promotionalTypes.filter((type) =>
      allApplicablePromotionalTypes.includes(type)
    );
  }, [promotionalTypes, allApplicablePromotionalTypes]);

  // Helper function to calculate earn rate with promotional types
  const calculateEarnRateWithPromotions = useCallback(
    (rate: CardRewardsEarnRate): number => {
      let earnRate = rate.earnRate;

      if (applicablePromotionalTypesForCard.length > 0 && rate.promotions) {
        const matchingPromotions = rate.promotions.filter((promotion) => {
          if (
            !promotion ||
            typeof promotion.earnRate !== 'number' ||
            promotion.isSignupBonusEarnRate ||
            promotion.signupBonusDurationMonths != null
          ) {
            return false;
          }

          if (promotion.promotional_type) {
            const promoTypeStr = String(promotion.promotional_type);
            return applicablePromotionalTypesForCard.some(
              (selectedType) => String(selectedType) === promoTypeStr
            );
          }

          return false;
        });

        if (matchingPromotions.length > 0) {
          earnRate = Math.max(
            earnRate,
            ...matchingPromotions.map((p) => p.earnRate)
          );
        }
      }

      return earnRate;
    },
    [applicablePromotionalTypesForCard]
  );

  // Get the promotional type display name and edit text
  const promotionalTypeDisplay = useMemo(() => {
    // If there are selected applicable types, show what's applied
    if (
      applicablePromotionalTypesForCard &&
      applicablePromotionalTypesForCard.length > 0
    ) {
      // For Preferred Rewards, use the tier name
      const preferredRewardsType = applicablePromotionalTypesForCard.find(
        (type) => PREFERRED_REWARDS_TIER_NAMES[type]
      );
      if (preferredRewardsType) {
        return {
          type: 'applied' as const,
          text: PREFERRED_REWARDS_TIER_NAMES[preferredRewardsType],
        };
      }

      // For Bilt Spend Ratio, show descriptive text
      const biltSpendRatioType = applicablePromotionalTypesForCard.find(
        (type) => BILT_SPEND_RATIO_TIER_NAMES[type]
      );
      if (biltSpendRatioType) {
        return {
          type: 'applied' as const,
          text: `Bilt Spend Ratio: ${BILT_SPEND_RATIO_TIER_NAMES[biltSpendRatioType]}`,
        };
      }

      // For other types, use the config label
      const firstType = applicablePromotionalTypesForCard[0];
      const config = promotionalTypesConfig[firstType];
      return {
        type: 'applied' as const,
        text: config?.label || firstType,
      };
    }

    // If there are applicable types but none selected, show edit prompt
    if (allApplicablePromotionalTypes.length > 0) {
      const grouped = groupPromotionalTypesByFamily(
        allApplicablePromotionalTypes
      );
      const firstFamily = Array.from(grouped.keys())[0];
      if (firstFamily === 'bank_of_america_preferred_rewards') {
        return {
          type: 'edit' as const,
          text: 'Bank of America Preferred Rewards',
        };
      } else if (firstFamily === 'us_bank_smartly') {
        return {
          type: 'edit' as const,
          text: 'U.S. Bank Smartly Tier',
        };
      } else if (firstFamily === 'bilt_spend_ratio') {
        return {
          type: 'edit' as const,
          text: 'Bilt Spend Ratio',
        };
      } else {
        // For other types, use the config label
        const firstType = allApplicablePromotionalTypes[0];
        const config = promotionalTypesConfig[firstType];
        return {
          type: 'edit' as const,
          text: config?.label || firstType,
        };
      }
    }

    return null;
  }, [applicablePromotionalTypesForCard, allApplicablePromotionalTypes]);

  // Calculate earn rates with promotional types factored in
  const categoryEarnRates = useCategoryEarnRatesAndCreditsV2(
    [cardInfo.id as CreditCard],
    allCategories,
    pointValues,
    true, // Assume purchasing in USA
    resolvedSelectedCategories
      ? { [cardInfo.id]: resolvedSelectedCategories }
      : undefined,
    applicablePromotionalTypesForCard.map(String) || []
  );

  // Get selectable categories for this card
  const selectableRates = useMemo(() => (cardInfo.rewards_earn_rates?.filter(
    (rate) =>
      isSelectableCategoriesEarnRate(rate) &&
      rate.category === 'selectable_categories'
  ) || []) as SelectableCategoriesEarnRate[], [cardInfo.rewards_earn_rates]);

  // Get quarterly categories for this card
  const quarterlyRates = (cardInfo.rewards_earn_rates?.filter(
    (rate) =>
      isQuarterlyCategoriesEarnRate(rate) &&
      rate.category === 'quarterly_categories'
  ) || []) as SelectableCategoriesEarnRate[];

  const fixedCategories = (cardInfo.rewards_earn_rates?.filter(
    (rate) =>
      isFixedCategoryGroupEarnRate(rate) &&
      rate.category === 'fixed_category_group'
  ) || []) as SelectableCategoriesEarnRate[];

  useEffect(() => {
    if (onSelectCategoriesForCard) {
      return;
    }
    if (localSelectedCategories.length > 0) {
      return;
    }
    if (selectableRates.length === 0) {
      return;
    }

    const initialSelections: string[][][] = [];
    selectableRates.forEach((rate, rateIndex) => {
      const { selectedCategories } = getSelectedCategoriesForRate({
        userSelectedCategoriesForCard: [],
        reward: rate,
        rateIndex,
      });
      initialSelections[rateIndex] = selectedCategories;
    });

    if (initialSelections.length > 0) {
      setLocalSelectedCategories(initialSelections);
    }
  }, [
    localSelectedCategories.length,
    onSelectCategoriesForCard,
    selectableRates,
  ]);

  return (
    <div className="card-modal-right-panel">
      <h2
        style={{
          margin: '0 0 16px 0',
          color: '#1a1a1a',
        }}
      >
        {cardInfo.name}
      </h2>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          margin: '0 0 24px 0',
        }}
      >
        <p
          style={{
            fontSize: '14px',
            color: '#000',
            margin: 0,
            fontFamily: 'Merriweather',
          }}
        >
          Earns {formatRewardsType(cardInfo.rewardsType)}
        </p>
        {cardInfo.rewardsType !== 'cash_back' &&
          pointValues[cardInfo.rewardsType] && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '4px 8px',
                backgroundColor: '#f5f5f5',
                borderRadius: '6px',
                fontSize: '12px',
              }}
            >
              <span style={{ color: '#666' }}>
                Estimate: {pointValues[cardInfo.rewardsType]}¢ per point
              </span>
              {setShowEditPointValues && (
                <button
                  onClick={() => setShowEditPointValues(true)}
                  style={{
                    color: '#1976d2',
                    textDecoration: 'none',
                    fontSize: '14px',
                    fontWeight: '500',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                  }}
                >
                  customize
                </button>
              )}
            </div>
          )}
      </div>

      <div style={{}}>
        {/* Promotional Type Display */}
        {promotionalTypeDisplay && (
          <div
            style={{
              marginBottom: '24px',
              padding: '8px 12px',
              backgroundColor:
                promotionalTypeDisplay.type === 'applied'
                  ? '#f0f8ff'
                  : 'transparent',
              borderRadius: '8px',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              justifyContent: 'space-between',
            }}
          >
            {promotionalTypeDisplay.type === 'applied' ? (
              <>
                <span style={{ color: '#333' }}>
                  {promotionalTypeDisplay.text} applied
                </span>
                {setShowEditPointValues && (
                  <button
                    onClick={() => setShowEditPointValues(true)}
                    style={{
                      color: '#1976d2',
                      textDecoration: 'none',
                      fontSize: '13px',
                      fontWeight: '500',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  >
                    Edit
                  </button>
                )}
              </>
            ) : (
              setShowEditPointValues && (
                <button
                  onClick={() => setShowEditPointValues(true)}
                  style={{
                    color: '#1976d2',
                    textDecoration: 'none',
                    fontSize: '13px',
                    fontWeight: '500',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    width: '100%',
                    textAlign: 'left',
                  }}
                >
                  Edit {promotionalTypeDisplay.text}
                </button>
              )
            )}
          </div>
        )}

        {/* Rewards Summary */}
        <CardInfoFlex title="Rewards summary:" backgroundColor="#f0f8ff" borderRadius="12px" padding="12px 14px" titleStyles={{ fontSize: '14px' }}>
          {(() => {
            // Build rewards summary using calculated earn rates with promotional types factored in
            const rewardsSummaryItems: Array<{
              category: string;
              earnRate: number;
              isSelectable?: boolean;
              isQuarterly?: boolean;
              isFixedGroup?: boolean;
            }> = [];

            // Add regular categories from calculated earn rates
            allCategories.forEach((category) => {
              const categoryData = categoryEarnRates[category];
              if (
                categoryData &&
                categoryData.sortedCardsByEarnRate.length > 0
              ) {
                const bestRate = categoryData.sortedCardsByEarnRate[0];
                if (bestRate.card === cardInfo.id && bestRate.earnRate) {
                  rewardsSummaryItems.push({
                    category: category as string,
                    earnRate: bestRate.earnRate,
                  });
                }
              }
            });

            // Also include selectable and quarterly categories from flattened rates
            // but use calculated rates with promotional types when available
            const flattenedRates = flattenRewardsEarnRates(
              cardInfo.rewards_earn_rates,
              resolvedSelectedCategories?.flat()?.flat() || [],
              cardInfo.rewardsType
            );

            flattenedRates.forEach((rate) => {
              // Only add if not already in summary (avoid duplicates)
              if (
                !rewardsSummaryItems.find(
                  (item) => item.category === rate.category
                )
              ) {
                // Try to find the calculated rate with promotional types for this category
                const categoryAsType = rate.category as CategoryType;
                const calculatedRate = categoryEarnRates[categoryAsType];
                let finalEarnRate = rate.earnRate;

                if (
                  calculatedRate &&
                  calculatedRate.sortedCardsByEarnRate.length > 0
                ) {
                  const bestRate = calculatedRate.sortedCardsByEarnRate[0];
                  if (bestRate.card === cardInfo.id && bestRate.earnRate) {
                    finalEarnRate = bestRate.earnRate;
                  }
                } else {
                  // Fallback: calculate from the original rate data
                  const rateData = cardInfo.rewards_earn_rates?.find((r) => {
                    if (rate.isSelectable) {
                      return isSelectableCategoriesEarnRate(r);
                    } else if (rate.isQuarterly) {
                      return isQuarterlyCategoriesEarnRate(r);
                    } else if (rate.isFixedGroup) {
                      return isFixedCategoryGroupEarnRate(r);
                    }
                    return r.category === rate.category;
                  });
                  if (rateData) {
                    finalEarnRate = calculateEarnRateWithPromotions(rateData);
                  }
                }

                rewardsSummaryItems.push({
                  category: rate.category,
                  earnRate: finalEarnRate,
                  isSelectable: rate.isSelectable,
                  isQuarterly: rate.isQuarterly,
                  isFixedGroup: rate.isFixedGroup,
                });
              }
            });

            if (rewardsSummaryItems.length === 0) {
              return (
                <span style={{ color: '#86A1B3', fontStyle: 'italic' }}>—</span>
              );
            }

            return (
              <>
                {rewardsSummaryItems.map((rate, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '5px 10px',
                      backgroundColor: '#fff',
                      color: '#333',
                      borderRadius: '8px',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      position: 'relative',
                    }}
                  >
                    {getCategoryEmoji(rate.category)}{' '}
                    {formatEarnRate(rate.earnRate, cardInfo.rewardsType)}{' '}
                    {formatSpendCategory(rate.category)}
                    {rate.isSelectable && (
                      <span style={{ fontSize: '10px' }}>*</span>
                    )}
                    {rate.isQuarterly && (
                      <span style={{ fontSize: '10px' }}>*</span>
                    )}
                    {rate.isFixedGroup && (
                      <span style={{ fontSize: '10px' }}>*</span>
                    )}
                  </div>
                ))}
              </>
            );
          })()}
        </CardInfoFlex>

        {/* Credits Summary */}
        {(() => {
          // Only show structured credits data - don't fall back to parsing benefits
          const credits = cardInfo.credits || [];
          const filteredCredits = credits.filter(
            (credit) =>
              credit.credit_type !==
              'american_express_venue_collection_concessions'
          );
          const creditBenefits: Array<{
            name: string;
            amount: number | null;
            frequency: string;
            valueUnit: string | null;
          }> = filteredCredits.map((credit) => ({
            name: credit.name,
            amount: credit.value,
            frequency: formatFrequencyText(credit.reward_frequency),
            valueUnit: credit.value_unit,
          }));

          return creditBenefits.length > 0 ? (
            <CardInfoFlex
              title="Credits summary:"
              backgroundColor="transparent"
            >
              {creditBenefits.map((credit, index) => (
                <CreditPill
                  key={index}
                  amount={credit.amount}
                  frequency={credit.frequency}
                  name={credit.name}
                  valueUnit={credit.valueUnit}
                />
              ))}
            </CardInfoFlex>
          ) : null;
        })()}

        {/* Key Information */}
        <CardInfoGrid
          title="Key Information"
          columns={2}
          backgroundColor="transparent"
        >
          <div>
            <p
              style={{
                margin: '0 0 4px 0',
                fontSize: '14px',
                fontWeight: '600',
                color: '#333',
              }}
            >
              Sign up bonus:
            </p>
            <p style={{ margin: 0, fontSize: '14px', color: '#333' }}>
              {formatSignupBonus(cardInfo.signupBonus, showReviewIndicators)}
            </p>
          </div>
          <div>
            <p
              style={{
                margin: '0 0 4px 0',
                fontSize: '14px',
                fontWeight: '600',
                color: '#333',
              }}
            >
              Annual fee:
            </p>
            <p style={{ margin: 0, fontSize: '14px', color: '#333' }}>
              {(() => {
                const annualFee =
                  cardInfo.fees?.annual_fee_amount_dollars ??
                  cardInfo.annualFee ??
                  0;
                const isWaivedFirstYear =
                  cardInfo.fees?.annual_fee_waived_first_year ?? false;

                if (annualFee === 0) {
                  return '$0';
                }

                if (isWaivedFirstYear) {
                  return (
                    <span>
                      $0 <span style={{ fontSize: '12px' }}>first year</span>
                      <br />${annualFee}{' '}
                      <span style={{ fontSize: '12px' }}>after</span>
                    </span>
                  );
                }

                return `$${annualFee}`;
              })()}
            </p>
          </div>
          <div>
            <p
              style={{
                margin: '0 0 4px 0',
                fontSize: '14px',
                fontWeight: '600',
                color: '#333',
              }}
            >
              Foreign transaction fee:
            </p>
            <p style={{ margin: 0, fontSize: '14px', color: '#333' }}>
              {cardInfo.foreignTransactionFee === 0
                ? 'None'
                : cardInfo.foreignTransactionFee
                ? `${cardInfo.foreignTransactionFee * 100}%`
                : 'Yes'}
            </p>
          </div>
          {/* <div>
            <p
              style={{
                margin: '0 0 4px 0',
                fontSize: '14px',
                fontWeight: '600',
                color: '#333',
              }}
            >
              APR:
            </p>
            <p style={{ margin: 0, fontSize: '14px', color: '#333' }}>
              {cardInfo.fees?.apr_low && cardInfo.fees?.apr_high
                ? `${(cardInfo.fees?.apr_low * 100).toFixed(2)}% - ${(
                    cardInfo.fees?.apr_high * 100
                  ).toFixed(2)}%`
                : 'None'}
            </p>
          </div> */}
        </CardInfoGrid>

        {/* Selectable Categories */}
        {selectableRates.length > 0 && (
          <CardInfoBlock
            title="Selectable categories:"
            backgroundColor="transparent"
          >
            {selectableRates.map((rate, rateIndex) => {
              const userSelectedCategoriesForCard: string[][][] =
                resolvedSelectedCategories || [];
              const { selectedCategories, allRewardCategories } =
                getSelectedCategoriesForRate({
                  userSelectedCategoriesForCard,
                  reward: rate,
                  rateIndex,
                });

              return (
                <div key={rateIndex} style={{ marginBottom: '16px' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      flexWrap: 'wrap',
                    }}
                  >
                    {selectedCategories.map((selectedCategoryGroup, index) => {
                      const dropdownKey = `${rateIndex}_${index}`;
                      const isOpen = openDropdowns[dropdownKey];

                      return (
                        <div
                          key={index}
                          style={{
                            position: 'relative',
                          }}
                        >
                          <div
                            style={{
                              padding: '6px 10px',
                              backgroundColor: '#4CAF50',
                              color: 'white',
                              borderRadius: '12px',
                              fontSize: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              cursor: 'pointer',
                              minWidth: '120px',
                              justifyContent: 'space-between',
                            }}
                            onClick={() => {
                              setOpenDropdowns((prev) => ({
                                ...prev,
                                [dropdownKey]: !isOpen,
                              }));
                            }}
                          >
                            <span
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                              }}
                            >
                              {formatEarnRate(
                                calculateEarnRateWithPromotions(rate),
                                cardInfo.rewardsType
                              )}{' '}
                              {selectedCategoryGroup.map((category: string) => (
                                <div key={category}>
                                  {getCategoryEmoji(category)}{' '}
                                  {formatSpendCategory(category)}
                                </div>
                              ))}
                            </span>
                            <span
                              style={{
                                fontSize: '10px',
                                transform: isOpen ? 'rotate(180deg)' : 'none',
                              }}
                            >
                              ▼
                            </span>
                          </div>

                          {/* Dropdown Menu */}
                          {isOpen && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                backgroundColor: 'white',
                                border: '1px solid #ddd',
                                borderRadius: '8px',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                zIndex: 1000,
                                marginTop: '4px',
                                maxHeight: '200px',
                                overflowY: 'auto',
                              }}
                            >
                              {allRewardCategories.map(
                                (categories, catIndex) => {
                                  // Check if this category is already selected in another pill within this rate group
                                  const currentRateSelections =
                                    userSelectedCategoriesForCard[rateIndex] ||
                                    [];
                                  const isAlreadySelected = Array.from(
                                    {
                                      length: currentRateSelections.length,
                                    },
                                    (_, pillIndex) => {
                                      if (pillIndex === index) {
                                        return false;
                                      } // Skip current pill
                                      const otherPillSelections =
                                        currentRateSelections[pillIndex];
                                      return otherPillSelections === categories;
                                    }
                                  ).some(Boolean);

                                  const isDisabled = isAlreadySelected;

                                  return (
                                    <div
                                      key={catIndex}
                                      style={{
                                        padding: '8px 12px',
                                        cursor: isDisabled
                                          ? 'not-allowed'
                                          : 'pointer',
                                        borderBottom:
                                          catIndex <
                                          allRewardCategories.length - 1
                                            ? '1px solid #f0f0f0'
                                            : 'none',
                                        backgroundColor:
                                          selectedCategories[index] ===
                                          categories
                                            ? '#f0f8ff'
                                            : isDisabled
                                            ? '#f5f5f5'
                                            : 'transparent',
                                        fontSize: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        opacity: isDisabled ? 0.5 : 1,
                                        color: isDisabled ? '#999' : 'inherit',
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (isDisabled) {
                                          return;
                                        } // Don't allow selection of disabled items

                                        const newSelections = [
                                          ...userSelectedCategoriesForCard,
                                        ];
                                        // Update the specific rate's selections
                                        if (!newSelections[rateIndex]) {
                                          newSelections[rateIndex] = [];
                                        }
                                        const rateSelections = [
                                          ...(newSelections[rateIndex] || []),
                                        ];
                                        rateSelections[index] = categories;
                                        newSelections[rateIndex] =
                                          rateSelections;
                                        setLocalSelectedCategories(
                                          newSelections
                                        );
                                        if (onSelectCategoriesForCard) {
                                          onSelectCategoriesForCard(
                                            newSelections
                                          );
                                        }
                                        setOpenDropdowns((prev) => ({
                                          ...prev,
                                          [dropdownKey]: false,
                                        }));
                                      }}
                                    >
                                      {categories.map(
                                        (category: string, index: number) => (
                                          <div
                                            key={index}
                                            style={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '4px',
                                            }}
                                          >
                                            {getCategoryEmoji(category)}{' '}
                                            {formatSpendCategory(category)}
                                          </div>
                                        )
                                      )}
                                      {isDisabled && (
                                        <span
                                          style={{
                                            fontSize: '10px',
                                            marginLeft: '4px',
                                          }}
                                        >
                                          ✓
                                        </span>
                                      )}
                                    </div>
                                  );
                                }
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <EarnRateConditionsMessage
                    rate={rate}
                    onEditBiltSpendRatio={
                      setPromotionalTypes
                        ? () => setShowBiltSpendRatioModal(true)
                        : undefined
                    }
                  />
                </div>
              );
            })}
          </CardInfoBlock>
        )}

        {/* Earn Rates */}
        <CardInfoBlock title="Earn rates:" backgroundColor="transparent">
          {(() => {
            const allRates = cardInfo.rewards_earn_rates || [];
            const fixedRates =
              cardInfo.rewards_earn_rates?.filter(
                (rate) =>
                  rate.category !== 'quarterly_categories' &&
                  rate.category !== 'selectable_categories' &&
                  rate.category !== 'fixed_category_group'
              ) || [];

            // Only show dash if there are no earn rates at all (including fixed categories)
            if (allRates.length === 0) {
              return (
                <span style={{ color: '#86A1B3', fontStyle: 'italic' }}>—</span>
              );
            }

            // Combine fixed rates and fixed categories
            const allEarnRates = [
              ...fixedRates.map((rate, index) => (
                <div
                  key={`fixed-rate-${index}`}
                  style={{
                    marginBottom: '16px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '4px',
                    }}
                  >
                    <div
                      style={{
                        padding: '5px 10px',
                        backgroundColor: '#EEF1F4',
                        color: '#333',
                        borderRadius: '8px',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      {getCategoryEmoji(rate.category)}{' '}
                      {formatEarnRate(
                        calculateEarnRateWithPromotions(rate),
                        cardInfo.rewardsType
                      )}{' '}
                      {formatSpendCategory(rate.category)}
                    </div>
                    <span style={{ fontSize: '14px', color: '#333' }}>
                      Earn{' '}
                      {formatEarnRate(
                        calculateEarnRateWithPromotions(rate),
                        cardInfo.rewardsType
                      )}{' '}
                      on {formatSpendCategory(rate.category)}
                    </span>
                  </div>
                  <EarnRateConditionsMessage
                    rate={rate}
                    onEditBiltSpendRatio={
                      setPromotionalTypes
                        ? () => setShowBiltSpendRatioModal(true)
                        : undefined
                    }
                  />
                </div>
              )),
              ...fixedCategories.map((cat, index) => (
                <div
                  key={`fixed-category-${index}`}
                  style={{
                    marginBottom: '16px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px',
                      marginBottom: '4px',
                    }}
                  >
                    {cat.selectableCategories.categories.map(
                      (category, catIndex) =>
                        isAdvancedCategory(category) ? (
                          category.categories.map((advancedCat, advIndex) => (
                            <CategoryEarnRatePill
                              key={`${catIndex}-${advIndex}`}
                              category={advancedCat}
                              earnRate={calculateEarnRateWithPromotions(cat)}
                              rewardsType={cardInfo.rewardsType}
                            />
                          ))
                        ) : (
                          <CategoryEarnRatePill
                            key={catIndex}
                            category={category}
                            earnRate={calculateEarnRateWithPromotions(cat)}
                            rewardsType={cardInfo.rewardsType}
                          />
                        )
                    )}
                  </div>
                  <EarnRateConditionsMessage
                    rate={cat}
                    onEditBiltSpendRatio={
                      setPromotionalTypes
                        ? () => setShowBiltSpendRatioModal(true)
                        : undefined
                    }
                  />
                </div>
              )),
            ];

            return allEarnRates.length === 0 ? null : allEarnRates;
          })()}
        </CardInfoBlock>

        {/* Quarterly Rotating Rewards */}
        {quarterlyRates.length > 0 &&
          (() => {
            // Get the flattened quarterly rates from the main rewards summary
            const allFlattenedRates = flattenRewardsEarnRates(
              cardInfo.rewards_earn_rates,
              resolvedSelectedCategories?.flat()?.flat() || [],
              cardInfo.rewardsType
            );

            const quarterlyFlattenedRates = allFlattenedRates.filter(
              (rate) => rate.isQuarterly
            );

            if (quarterlyFlattenedRates.length === 0) {
              return null;
            }

            const currentQuarter = getCurrentQuarter();

            return (
              <CardInfoFlex
                title={`Quarterly rotating rewards (${currentQuarter}):`}
                backgroundColor="transparent"
              >
                {quarterlyFlattenedRates.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '5px 10px',
                      backgroundColor: '#FFE4B5',
                      color: '#333',
                      borderRadius: '8px',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      position: 'relative',
                    }}
                  >
                    {getCategoryEmoji(item.category)}{' '}
                    {formatEarnRate(item.earnRate, cardInfo.rewardsType)}{' '}
                    {formatSpendCategory(item.category)}
                  </div>
                ))}
                <p
                  style={{
                    fontSize: '12px',
                    color: '#666',
                    margin: '8px 0 0 0',
                  }}
                >
                  Categories rotate quarterly. Current quarter: {currentQuarter}
                  . Requires activation.
                  {(() => {
                    // Check if any quarterly rate has an end date
                    const endDate = quarterlyRates.find((rate) =>
                      extractEndDate(rate.description)
                    );
                    return endDate
                      ? ` Valid through ${extractEndDate(endDate.description)}.`
                      : '';
                  })()}
                </p>
              </CardInfoFlex>
            );
          })()}
      </div>

      <p
        style={{
          fontSize: '12px',
          color: '#999',
          margin: '24px 0 0 0',
          lineHeight: '1.5',
        }}
      >
        We work hard to keep this info accurate, but it may not always be up to
        date. Please confirm details on the card issuer's site before applying
        or making decisions.
      </p>

      {/* Edit Point Values Modal */}
      {showEditPointValues &&
        setShowEditPointValues &&
        setPointValues &&
        resetPointValues && (
          <EditPointValues
            handleClose={() => setShowEditPointValues(false)}
            cards={[cardInfo.id]}
            pointValues={pointValues}
            setPointValues={setPointValues}
            resetPointValues={resetPointValues}
            promotionalTypes={promotionalTypes}
            setPromotionalTypes={setPromotionalTypes}
            resetPromotionalTypes={resetPromotionalTypes}
          />
        )}

      {/* Bilt Spend Ratio Modal */}
      {showBiltSpendRatioModal && setPromotionalTypes && (
        <BiltSpendRatioModal
          handleClose={() => setShowBiltSpendRatioModal(false)}
          promotionalTypes={promotionalTypes}
          setPromotionalTypes={setPromotionalTypes}
        />
      )}
    </div>
  );
};
